import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Strings from '../../Strings';
import { actions } from './orgFilterActions';
import { isSuperUser } from '../../utils/userRoles';
import { getOrganizationHierarchyRows } from '../../pages/Organizations/organizationHierarchy';
import Multiselect from '../Select/Multiselect';

export const NO_ORG = 'no_org';

const getHierarchyOptions = organizations => {
  return getOrganizationHierarchyRows(organizations).map(organization => ({
    ...organization,
    value: organization.masked_id,
    label: organization.name,
  }));
};

function HierarchyOptionLabel({ option }) {
  const checkboxRef = React.useRef(null);

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = option.isCheckboxIndeterminate;
    }
  }, [option.isCheckboxIndeterminate]);

  return (
    <span
      className="org-hierarchy-option-content"
      style={{ '--org-hierarchy-depth': option.hierarchyDepth || 0 }}
    >
      {!option.noCheckbox && (
        <input
          key={`${option.value}_${option.isCheckboxChecked}`}
          ref={checkboxRef}
          className="org-hierarchy-option-checkbox"
          type="checkbox"
          defaultChecked={option.isCheckboxChecked}
          disabled={option.isDisabled}
        />
      )}
      <span className={`org-hierarchy-option-label ${option.isHierarchyBranchHeader ? 'parent' : ''}`}>
        {option.label}
      </span>
    </span>
  );
}

HierarchyOptionLabel.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hierarchyBranchIds: PropTypes.arrayOf(PropTypes.string),
    hierarchyDepth: PropTypes.number,
    hasHierarchyChildren: PropTypes.bool,
    isHierarchyBranchHeader: PropTypes.bool,
    isCheckboxChecked: PropTypes.bool,
    isCheckboxIndeterminate: PropTypes.bool,
    noCheckbox: PropTypes.bool,
    isDisabled: PropTypes.bool,
  }).isRequired,
};

const getHierarchyOptionLabel = option => <HierarchyOptionLabel option={option} />;

  // Helper function to group organizations by parent
export const groupOrganizations = (orgData) => {
    const orgMap = new Map();
    const parentOrgs = [];
    const childOrgs = [];

    // Separate parent and child organizations
    orgData.forEach(org => {
      orgMap.set(org.masked_id, { ...org, value: org.masked_id, label: org.name });
      if (org.parent_org_id) {
        childOrgs.push(org);
      } else {
        parentOrgs.push(org);
      }
    });

    const groupedOptions = [];
    const individualOptions = [];

    // Group children under their parents
    parentOrgs.forEach(parent => {
      const parentOption = { ...parent, value: parent.masked_id, label: parent.name };
      const children = childOrgs
        .filter(child => child.parent_org_id === parent.masked_id)
        .map(child => ({ ...child, value: child.masked_id, label: child.name }));

      if (children.length > 0) {
        // Create grouped option for parent with children
        groupedOptions.push({
          label: parent.name,
          options: [parentOption, ...children]
        });
      } else {
        // Parent with no children - add as regular option
        individualOptions.push(parentOption);
      }
    });

    // Add orphaned children (children whose parents are not in the list) as individual options
    childOrgs.forEach(child => {
      const hasParentInList = parentOrgs.some(parent => parent.masked_id === child.parent_org_id);
      if (!hasParentInList) {
        individualOptions.push({ ...child, value: child.masked_id, label: child.name });
      }
    });

    // Return grouped options first, then individual options
    return [...groupedOptions, ...individualOptions];
  };

function OrgSelect(props) {
  const { getOrgs, orgIds, setOrgFilter, addNoOrg } = props;
  const [organizations, setOrganizations] = React.useState([]);
  const selectedOrganizationsCount = orgIds?.length || 0;
  const selectedIds = React.useMemo(() => new Set(orgIds || []), [orgIds]);
  const hierarchyOptions = React.useMemo(() => getHierarchyOptions(organizations), [organizations]);
  const orgs = React.useMemo(() => hierarchyOptions.map(option => ({
    ...option,
    hierarchyBranchIds: [option.value],
    hierarchyDepth: option.hierarchyOptionDepth,
    isCheckboxChecked: selectedIds.has(option.value),
    isCheckboxIndeterminate: false,
  })), [hierarchyOptions, selectedIds]);
  const displayedOrgs = React.useMemo(() => hierarchyOptions.flatMap((option, index) => {
    const organizationOption = orgs[index];
    if (!option.hasHierarchyChildren) {
      return [organizationOption];
    }

    const selectedBranchIds = option.hierarchyBranchIds.filter(id => selectedIds.has(id));
    const branchOption = {
      ...option,
      value: `hierarchy_branch_${option.value}`,
      hierarchyDepth: option.hierarchyBranchDepth,
      rendersAsHeader: true,
      isHierarchyBranchHeader: true,
      isCheckboxChecked: selectedBranchIds.length === option.hierarchyBranchIds.length,
      isCheckboxIndeterminate:
        selectedBranchIds.length > 0 && selectedBranchIds.length < option.hierarchyBranchIds.length,
    };

    return [branchOption, organizationOption];
  }), [hierarchyOptions, orgs, selectedIds]);

  React.useEffect(() => {
    getOrgs().then(resp => {
      const orgData = resp.response?.data;
      if (Array.isArray(orgData)) {
        setOrganizations(orgData);
        
        // Check if current orgIds exist in any of the organizations
        const allOrgIds = orgData.map(o => o.masked_id);
        const validOrgIds = (orgIds || []).filter(
          id => allOrgIds.includes(id) || (id === NO_ORG && addNoOrg && isSuperUser()),
        );
        if (orgData.length > 0 && validOrgIds.length !== (orgIds || []).length) {
          setOrgFilter(validOrgIds);
        }
      } else {
        setOrganizations([]);
      }
    });
    if ((orgIds || []).includes(NO_ORG) && !addNoOrg) {
      setOrgFilter((orgIds || []).filter(id => id !== NO_ORG));
    }
  }, []);

  return orgs?.length > 1 ? (
    <Multiselect
      id="org-select"
      data={
        (addNoOrg && isSuperUser() ? [
          {
            label: Strings.patientDashboard.noOrg,
            value: NO_ORG,
            rendersAsHeader: true,
            isCheckboxChecked: selectedIds.has(NO_ORG),
          },
          { label: '── OR select organizations: ──', value: 'separator', isDisabled: true, rendersAsHeader: true, noCheckbox: true },
        ] : [])
          .concat(displayedOrgs)
      }
      value={orgIds || []}
      onChange={(options, actionMeta) => {
        const changedOption = actionMeta?.option || actionMeta?.removedValue;

        if (!changedOption) {
          setOrgFilter(options.filter(option => option.value !== 'separator').map(option => option.value));
          return;
        }

        if (changedOption.value === NO_ORG) {
          setOrgFilter(selectedIds.has(NO_ORG) ? [] : [NO_ORG]);
          return;
        }

        if (changedOption.value === 'separator') {
          return;
        }

        const nextIds = new Set((orgIds || []).filter(id => id !== NO_ORG));
        const branchIds = changedOption.hierarchyBranchIds || [changedOption.value];
        const entireBranchSelected = branchIds.every(id => nextIds.has(id));
        const removingValue = actionMeta.action === 'remove-value' || actionMeta.action === 'pop-value';

        if (entireBranchSelected || removingValue) {
          branchIds.forEach(id => nextIds.delete(id));
        } else {
          branchIds.forEach(id => nextIds.add(id));
        }

        setOrgFilter(orgs.filter(option => nextIds.has(option.value)).map(option => option.value));
      }}
      getLabel={getHierarchyOptionLabel}
      isSearchable
      closeMenuOnSelect={false}
      controlShouldRenderValue={false}
      placeholder={selectedOrganizationsCount === 0
        ? Strings.selectOrganizations
        : Strings.formatString(
          selectedOrganizationsCount === 1 ? Strings.organizationSelected : Strings.organizationsSelected,
          selectedOrganizationsCount,
        )}
      checkboxFirst
      className="org-hierarchy-select"
    />
  ) : null;
  };

OrgSelect.propTypes = {
  getOrgs: PropTypes.func,
  orgFilter: PropTypes.array,
  setOrgFilter: PropTypes.func,
  orgIds: PropTypes.array,
  addNoOrg: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    orgIds: state.entities?.orgFilter?.orgIds,
  };
};

const mapDispatchToProps = {
  setOrgFilter: actions.setOrgFilter,
  getOrgs: actions.getMyOrgs,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrgSelect);
